import { useState, useRef, useEffect, type ReactNode } from 'react'
import { ChevronDown, Bookmark, BookmarkCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NavProgressBar } from '@/components/NavProgressBar'

interface ProgressBarProps {
  current: number   // 1-indexed
  total: number
  onNavigate?: (index: number) => void   // called with 0-based index
  flaggedIds?: string[]
  questionIds?: string[]
  isFlagged?: boolean
  onFlag?: () => void
  /**
   * A control that belongs to the question itself rather than to the run of
   * them — the info button. Rendered after the flag, in the same row, so the
   * bar stays the one place a question is identified.
   */
  info?: ReactNode
}

export function ProgressBar({ current, total, onNavigate, flaggedIds, questionIds, isFlagged, onFlag, info }: ProgressBarProps) {
  const percentage = total > 0 ? Math.min(100, (current / total) * 100) : 0
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [open])

  // Scroll active item into view when dropdown opens
  useEffect(() => {
    if (!open || !listRef.current) return
    const active = listRef.current.querySelector<HTMLElement>('[data-active="true"]')
    active?.scrollIntoView({ block: 'nearest' })
  }, [open])

  function handleToggleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      // Focus first item after opening
      requestAnimationFrame(() => {
        const first = listRef.current?.querySelector<HTMLElement>('button')
        first?.focus()
      })
    }
  }

  function handleListKeyDown(e: React.KeyboardEvent) {
    const items = listRef.current ? Array.from(listRef.current.querySelectorAll<HTMLElement>('button')) : []
    const focused = document.activeElement as HTMLElement
    const idx = items.indexOf(focused)
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      toggleRef.current?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      items[Math.min(idx + 1, items.length - 1)]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (idx === 0) {
        setOpen(false)
        toggleRef.current?.focus()
      } else {
        items[idx - 1]?.focus()
      }
    } else if (e.key === 'Home') {
      e.preventDefault()
      items[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      items[items.length - 1]?.focus()
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-2 text-base text-muted-foreground">
        <div className="flex min-w-0 items-center gap-1">
        {onNavigate ? (
          <div ref={containerRef} className="relative">
            <button
              ref={toggleRef}
              type="button"
              aria-expanded={open}
              aria-haspopup="listbox"
              onClick={() => setOpen(o => !o)}
              onKeyDown={handleToggleKeyDown}
              className="flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-muted/40 px-3 py-2 text-[15px] sm:text-base font-medium transition-colors hover:bg-accent hover:text-foreground"
            >
              Question {current} of {total}
              <ChevronDown className={cn('h-4 w-4 transition-transform text-muted-foreground', open && 'rotate-180')} />
            </button>

            {open && (
              <div
                ref={listRef}
                role="listbox"
                aria-label="Jump to question"
                onKeyDown={handleListKeyDown}
                className="absolute top-full left-0 mt-1 z-50 bg-popover rounded-lg shadow-lg max-h-72 overflow-y-auto min-w-[200px]"
              >
                {Array.from({ length: total }, (_, i) => {
                  const isItemFlagged = !!(flaggedIds && questionIds && flaggedIds.includes(questionIds[i] ?? ''))
                  const isCurrent = i === current - 1
                  return (
                    <button
                      key={i}
                      type="button"
                      role="option"
                      aria-selected={isCurrent}
                      aria-label={`Go to question ${i + 1}${isItemFlagged ? ' (flagged)' : ''}`}
                      data-active={isCurrent}
                      onClick={() => { onNavigate(i); setOpen(false) }}
                      className={cn(
                        'w-full text-left px-3 py-3 text-base flex items-center justify-between gap-4 transition-colors hover:bg-accent',
                        isCurrent && 'bg-accent font-semibold text-foreground'
                      )}
                    >
                      <span>Question {i + 1}</span>
                      {isItemFlagged && <span aria-hidden="true" className="text-xs">🚩</span>}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          <span className="px-1 font-medium">Question {current} of {total}</span>
        )}
        {onFlag && (
          <button
            type="button"
            onClick={onFlag}
            aria-label={isFlagged ? 'Remove flag' : 'Flag question'}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-sm font-medium transition-colors hover:bg-accent sm:px-3',
              isFlagged ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {isFlagged
              ? <BookmarkCheck className="h-4 w-4" />
              : <Bookmark className="h-4 w-4" />}
            {isFlagged ? 'Flagged' : 'Flag'}
          </button>
        )}
        {info}
        </div>
        {/* The bar under the row already says how far in; on a phone the
            figure gives its width to the three controls beside it. */}
        <span className="hidden shrink-0 text-sm tabular-nums sm:inline">{Math.round(percentage)}%</span>
      </div>
      {/* The bar is the second way to jump between questions, alongside the
          dropdown above it: press or drag along it to go straight to a question,
          which on a 60-question mock beats scrolling a 60-row list. Same control
          as every other progress bar in the app, in the quiz's own colours. */}
      <NavProgressBar
        current={current}
        total={total}
        label={`Question ${current} of ${total}`}
        trackClassName="h-2.5 rounded-full bg-secondary group-hover:h-3 group-focus-visible:h-3"
        fillClassName="bg-foreground dark:bg-foreground"
        onScrub={onNavigate ? position => onNavigate(position - 1) : undefined}
        formatValue={position => `Question ${position} of ${total}`}
      />
    </div>
  )
}
